import { mockConflictError, mockForbiddenError, mockNotFoundError, mockValidationError } from "../mockDomainError";
import { __MOCK_STORY_ID__, listMockFilterableItems, mockFetchCanvasItem } from "./storyCanvasApi.mock";
import { applyStoryFiltersToItem, toMockDrillState } from "./storyFilterItemState.mock";
import type { CanvasItemFullApi } from "./storyCanvas.types";
import { findCatalogField, findMockFieldHierarchy, validateFilterValue } from "./storyFilters.mockRules";
import type {
  StoryFilterApi,
  StoryFilterCreateRequestApi,
  StoryFilterListParams,
  StoryFilterListResponseApi,
  StoryFilterMutationResponseApi,
  StoryFilterPatchRequestApi,
  StoryFilterRewindRequestApi,
} from "./storyFilters.types";

/**
 * Mock `/filters` store. A `global` row on Channel and a `baseline` on the
 * editable chart's Category, so later slices can show a story filter reaching
 * some charts and overriding a baseline on another.
 */

const LATENCY_MS = 150;
const BASELINE_ITEM_ID = "widget-chart-1";

export type MockStoryRole = "editor" | "viewer";

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const cloneJson = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const baselineId = (itemId: string, id: string) => `baseline:${itemId}:${id}`;

const seedFilters = (): StoryFilterApi[] => [
  {
    id: "flt-global-channel",
    kind: "global",
    seq: 1,
    canvas_item_id: null,
    field: { kind: "degenerate_dimension", model_field_id: "channel" },
    field_name: "Channel",
    value_type: "categorical",
    comparator: "in",
    value: ["Online"],
    hierarchy: null,
    hierarchy_id: null,
    hierarchy_level_id: null,
    created_by: "mock-user-1",
    created_at: "2026-10-13T09:00:00Z",
  },
  {
    id: baselineId(BASELINE_ITEM_ID, "category"),
    kind: "baseline",
    seq: null,
    canvas_item_id: BASELINE_ITEM_ID,
    field: { kind: "dimension_attribute", model_field_id: "category" },
    field_name: "Category",
    value_type: "categorical",
    comparator: "in",
    value: ["Technology"],
    hierarchy: null,
    hierarchy_id: null,
    hierarchy_level_id: null,
    created_by: "mock-user-1",
    created_at: "2026-10-13T09:00:00Z",
  },
];

let storedFilters: StoryFilterApi[] = seedFilters();
let nextFilterNumber = 1;
let role: MockStoryRole = "editor";

/** Flips `can_edit` for the whole mock, so the viewer state is reachable in the browser. */
export const setMockStoryRole = (next: MockStoryRole): void => {
  role = next;
};

/** The stored rows the item GET reads to decide what applies to each item. */
export const listMockStoryFilterRows = (): StoryFilterApi[] => cloneJson(sortFilters(storedFilters));

export const resetMockStoryFilters = (): void => {
  storedFilters = seedFilters();
  nextFilterNumber = 1;
  role = "editor";
};

/** Stored rows by `seq`, then baselines grouped by item, in first-seen order. */
const sortFilters = (filters: StoryFilterApi[]): StoryFilterApi[] => {
  const stored = filters
    .filter((filter) => filter.kind !== "baseline")
    .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  const baselines = filters.filter((filter) => filter.kind === "baseline");
  const itemOrder = Array.from(new Set(baselines.map((filter) => filter.canvas_item_id)));
  baselines.sort(
    (a, b) => itemOrder.indexOf(a.canvas_item_id) - itemOrder.indexOf(b.canvas_item_id),
  );
  return [...stored, ...baselines];
};

/** The Drug X memo story has no v2 items or filters, as with the items mock. */
export const assertStoryExists = (storyId: string): void => {
  if (!storyId || storyId === __MOCK_STORY_ID__) {
    throw mockNotFoundError(`Story not found: ${storyId}`);
  }
};

export async function mockFetchStoryFilters(
  storyId: string,
  params: StoryFilterListParams = {},
): Promise<StoryFilterListResponseApi> {
  await wait(LATENCY_MS);
  assertStoryExists(storyId);
  const { canvasItemId, kinds } = params;
  const filters = sortFilters(storedFilters).filter(
    (filter) =>
      (!canvasItemId || filter.canvas_item_id === canvasItemId) &&
      (!kinds?.length || kinds.includes(filter.kind)),
  );
  return { can_edit: role === "editor", filters: cloneJson(filters) };
}

// ─── Writes ────────────────────────────────────────────────────────────────────

const MOCK_USER_ID = "mock-user-1";

/**
 * A write by a viewer is the backend's 403, checked once the story is known to exist.
 * Item writes (add, patch, delete, move) share the same rule, so the items mock calls this too.
 */
export const assertMockStoryEditor = (
  message = "Only the story owner or an editor can change filters.",
): void => {
  if (role !== "editor") throw mockForbiddenError(message);
};

const assertCanEdit = (): void => assertMockStoryEditor();

const nextSeq = (): number => storedFilters.reduce((max, row) => Math.max(max, row.seq ?? 0), 0) + 1;

const outcome = (item: CanvasItemFullApi) =>
  JSON.stringify([item.spec, item.filters_applied, item.filters_not_applicable, item.filters_overridden, item.drill]);

/** Items whose rows or filter verdicts differ under `after` instead of `before`. */
const affectedItemIds = (before: StoryFilterApi[], after: StoryFilterApi[]): string[] =>
  listMockFilterableItems()
    .filter(
      (item) =>
        outcome(applyStoryFiltersToItem(item, before)) !== outcome(applyStoryFiltersToItem(item, after)),
    )
    .map((item) => item.id);

const toMutationResponse = (
  filter: StoryFilterApi | null,
  deletedIds: string[],
  before: StoryFilterApi[],
): StoryFilterMutationResponseApi => {
  const filters = sortFilters(storedFilters);
  const affected = new Set(affectedItemIds(before, filters));
  // A removed row's own chart changes too, even when its rows came out the same.
  const removedItems = before
    .filter((row) => deletedIds.includes(row.id) && row.canvas_item_id)
    .map((row) => row.canvas_item_id as string);
  removedItems.forEach((id) => affected.add(id));
  return {
    filter: filter ? cloneJson(filter) : null,
    deleted_filter_ids: deletedIds,
    affected_item_ids: Array.from(affected),
    filters: cloneJson(filters),
  };
};

const sameField = (a: StoryFilterApi, ref: { model_field_id: string; role?: string | null }): boolean =>
  a.field.model_field_id === ref.model_field_id && (a.field.role ?? null) === (ref.role ?? null);

interface CreatedRows {
  row: StoryFilterApi;
  deletedIds: string[];
}

/** Rows a new row displaces on the same field; `null` skips the delete. */
const store = (row: StoryFilterApi, deletedIds: string[]): CreatedRows => {
  storedFilters = [...storedFilters.filter((existing) => !deletedIds.includes(existing.id)), row];
  return { row, deletedIds };
};

const toRow = (
  kind: StoryFilterApi["kind"],
  canvasItemId: string | null,
  body: { field: StoryFilterApi["field"]; comparator: StoryFilterApi["comparator"] },
  field: { name: string; valueType: StoryFilterApi["value_type"] },
  value: StoryFilterApi["value"],
): StoryFilterApi => ({
  id: `flt-${nextFilterNumber++}`,
  kind,
  seq: nextSeq(),
  canvas_item_id: canvasItemId,
  field: body.field,
  field_name: field.name,
  value_type: field.valueType,
  comparator: body.comparator,
  value,
  // The field's own level, so a date chip reads at its grain; a drill step sets its hierarchy after.
  hierarchy: findMockFieldHierarchy(body.field.model_field_id),
  hierarchy_id: null,
  hierarchy_level_id: null,
  created_by: MOCK_USER_ID,
  created_at: new Date().toISOString(),
});

const createGlobal = (body: Extract<StoryFilterCreateRequestApi, { kind: "global" }>): CreatedRows => {
  const field = findCatalogField(body.field);
  if (!field) throw mockValidationError(`Unknown field: ${body.field.model_field_id}`);
  if (field.kind === "measure") throw mockValidationError("A measure can't be a story-wide filter.");
  const value = validateFilterValue(field, body.comparator, body.value);
  if (storedFilters.some((row) => row.kind === "global" && sameField(row, body.field))) {
    throw mockConflictError(`${field.name} already has a story filter. Edit it instead.`);
  }

  const row = toRow("global", null, body, field, value);
  // A story-wide filter wins over a chart's or an Include/Exclude on the same field.
  const deletedIds = storedFilters
    .filter((existing) => (existing.kind === "local" || existing.kind === "cross") && sameField(existing, body.field))
    .map((existing) => existing.id);
  return store(row, deletedIds);
};

const createCross = (body: Extract<StoryFilterCreateRequestApi, { kind: "cross" }>): CreatedRows => {
  const field = findCatalogField(body.field);
  if (!field) throw mockValidationError(`Unknown field: ${body.field.model_field_id}`);
  if (field.kind === "measure") throw mockValidationError("A measure can't be a story-wide filter.");
  if (!Array.isArray(body.value) || body.value.length !== 1) {
    throw mockValidationError("Include and Exclude take exactly one value.");
  }
  const value = validateFilterValue(field, body.comparator, body.value);
  if (storedFilters.some((row) => row.kind === "global" && sameField(row, body.field))) {
    throw mockConflictError(`${field.name} already has a story filter, which wins over Include and Exclude.`);
  }

  // A repeated Include or Exclude on a field replaces the earlier one, which moves to the trail's end.
  const deletedIds = storedFilters
    .filter((existing) => existing.kind === "cross" && sameField(existing, body.field))
    .map((existing) => existing.id);
  return store(toRow("cross", null, body, field, value), deletedIds);
};

/** The chart's own `drill_down` rows, in the order taken. */
const drillRowsOf = (itemId: string): StoryFilterApi[] =>
  storedFilters
    .filter((row) => row.kind === "drill_down" && row.canvas_item_id === itemId)
    .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));

const createDrillDown = async (
  storyId: string,
  body: Extract<StoryFilterCreateRequestApi, { kind: "drill_down" }>,
): Promise<CreatedRows> => {
  const item = await mockFetchCanvasItem(storyId, body.canvas_item_id);
  if (item.item_type !== "chart_2") {
    throw mockConflictError(`Only an editable chart can drill down: ${body.canvas_item_id}`);
  }
  if (body.value !== null && typeof body.value === "object") {
    throw mockValidationError("A drill step takes the clicked member as a single value.");
  }
  const raw = listMockFilterableItems().find((candidate) => candidate.id === body.canvas_item_id);
  const drill = raw ? toMockDrillState(raw, drillRowsOf(body.canvas_item_id), body.field) : null;
  if (!drill) {
    throw mockValidationError(
      body.field ? `${body.field.model_field_id} has no drillable hierarchy.` : "This chart's axis has no hierarchy to drill through.",
    );
  }
  // The chart's own one-click "Drill down" always advances one level, so it needs a level below.
  // Picking a field from the field picker's Hierarchy tab targets that level directly — the
  // leaf is a valid, terminal pick, not a "no more levels" error.
  if (!body.field && !drill.can_drill_down) throw mockValidationError("This chart is already at the last level.");

  const level = drill.levels.find((candidate) => candidate.level_no === drill.current_level_no);
  if (!level) throw mockValidationError("This chart's drill position is unknown.");
  const row = toRow("drill_down", body.canvas_item_id, { field: level.field, comparator: "eq" }, { name: level.name, valueType: "categorical" }, body.value);
  row.hierarchy = {
    id: drill.hierarchy_id,
    name: drill.hierarchy_name,
    level_no: level.level_no,
    levels_total: drill.levels.length,
  };
  row.hierarchy_id = drill.hierarchy_id;
  row.hierarchy_level_id = `${drill.hierarchy_id}-level-${level.level_no}`;
  // The drilled member replaces this chart's own filter on the level it came from.
  const deletedIds = storedFilters
    .filter((existing) => existing.kind === "local" && existing.canvas_item_id === body.canvas_item_id && sameField(existing, level.field))
    .map((existing) => existing.id);
  return store(row, deletedIds);
};

/** The ids of the fields a chart draws, so a measure filter can be held to them. */
const renderedFieldIds = (item: CanvasItemFullApi): Set<string> =>
  new Set(
    [item.binding?.x, ...(item.binding?.y ?? []), item.binding?.color_by]
      .flatMap((binding) => (binding ? [binding.field.model_field_id] : [])),
  );

const createLocal = async (
  storyId: string,
  body: Extract<StoryFilterCreateRequestApi, { kind: "local" }>,
): Promise<CreatedRows> => {
  const item = await mockFetchCanvasItem(storyId, body.canvas_item_id);
  if (item.item_type !== "chart_2") {
    throw mockConflictError(`Only an editable chart takes a chart filter: ${body.canvas_item_id}`);
  }
  const field = findCatalogField(body.field);
  if (!field) throw mockValidationError(`Unknown field: ${body.field.model_field_id}`);
  if (field.kind === "measure" && !renderedFieldIds(item).has(field.id)) {
    throw mockValidationError(`${field.name} is not drawn by this chart, so it can't filter it.`);
  }
  const value = validateFilterValue(field, body.comparator, body.value);
  if (
    storedFilters.some(
      (row) => row.kind === "local" && row.canvas_item_id === body.canvas_item_id && sameField(row, body.field),
    )
  ) {
    throw mockConflictError(`${field.name} already has a filter on this chart. Edit it instead.`);
  }

  return store(toRow("local", body.canvas_item_id, body, field, value), []);
};

export async function mockCreateStoryFilter(
  storyId: string,
  body: StoryFilterCreateRequestApi,
): Promise<StoryFilterMutationResponseApi> {
  await wait(LATENCY_MS);
  assertStoryExists(storyId);
  assertCanEdit();
  const before = sortFilters(storedFilters);

  const { row, deletedIds } =
    body.kind === "global"
      ? createGlobal(body)
      : body.kind === "cross"
        ? createCross(body)
        : body.kind === "local"
          ? await createLocal(storyId, body)
          : await createDrillDown(storyId, body);
  return toMutationResponse(row, deletedIds, before);
}

// ─── PATCH ─────────────────────────────────────────────────────────────────────

const TRAIL_KINDS = new Set<StoryFilterApi["kind"]>(["global", "cross", "drill_down"]);

const trailRows = (): StoryFilterApi[] =>
  storedFilters.filter((row) => TRAIL_KINDS.has(row.kind)).sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));

const findRow = (filterId: string): StoryFilterApi => {
  const row = storedFilters.find((candidate) => candidate.id === filterId);
  if (!row) throw mockNotFoundError(`Filter not found: ${filterId}`);
  return row;
};

/** The trail is last-in-first-out: only its latest step may change, and a drill step never. */
const assertEditable = (row: StoryFilterApi): void => {
  if (row.kind === "drill_down") {
    throw mockConflictError("A drill step can't be edited. Drill up, then drill down again.");
  }
  if (TRAIL_KINDS.has(row.kind)) {
    const latest = trailRows().slice(-1)[0];
    if (latest?.id !== row.id) {
      throw mockConflictError("Only the latest step in the trail can be edited.");
    }
  }
};

export async function mockPatchStoryFilter(
  storyId: string,
  filterId: string,
  body: StoryFilterPatchRequestApi,
): Promise<StoryFilterMutationResponseApi> {
  await wait(LATENCY_MS);
  assertStoryExists(storyId);
  assertCanEdit();
  const row = findRow(filterId);
  assertEditable(row);
  if (body.comparator === undefined && body.value === undefined) {
    throw mockValidationError("Send a comparator, a value, or both.");
  }

  const comparator = body.comparator ?? row.comparator;
  if (row.kind === "cross" && comparator !== "in" && comparator !== "not_in") {
    throw mockValidationError("Include and Exclude are in or not_in.");
  }
  const field = findCatalogField(row.field);
  if (!field) throw mockValidationError(`Unknown field: ${row.field.model_field_id}`);
  const nextValue = body.value ?? row.value;
  if (row.kind === "cross" && (!Array.isArray(nextValue) || nextValue.length !== 1)) {
    throw mockValidationError("Include and Exclude take exactly one value.");
  }
  const value = validateFilterValue(field, comparator, nextValue);

  const before = sortFilters(storedFilters);
  const updated: StoryFilterApi = { ...row, comparator, value };
  storedFilters = storedFilters.map((candidate) => (candidate.id === filterId ? updated : candidate));
  return toMutationResponse(updated, [], before);
}

// ─── KEEP ──────────────────────────────────────────────────────────────────────

/** The owner keeps a personal filter for everyone; a baseline is never one, so it 404s. */
export async function mockKeepStoryFilter(storyId: string, filterId: string): Promise<StoryFilterMutationResponseApi> {
  await wait(LATENCY_MS);
  assertStoryExists(storyId);
  assertCanEdit();
  const row = findRow(filterId);
  if (row.kind === "baseline") throw mockNotFoundError(`Filter not found: ${filterId}`);

  const before = sortFilters(storedFilters);
  const kept: StoryFilterApi = { ...row, personal: false };
  storedFilters = storedFilters.map((candidate) => (candidate.id === filterId ? kept : candidate));
  return toMutationResponse(kept, [], before);
}

// ─── DELETE ────────────────────────────────────────────────────────────────────

/**
 * The trail is last-in-first-out: a step may go only when nothing after it
 * would be left. The exception is a drill step, which takes its chart's later
 * drill steps with it (drilling up past them), so only another chart's step or
 * a story-wide one after it blocks the delete.
 */
const rowsRemovedWith = (row: StoryFilterApi): StoryFilterApi[] => {
  if (!TRAIL_KINDS.has(row.kind)) return [row];
  const later = trailRows().filter((candidate) => (candidate.seq ?? 0) > (row.seq ?? 0));
  const cascade =
    row.kind === "drill_down"
      ? later.filter((candidate) => candidate.kind === "drill_down" && candidate.canvas_item_id === row.canvas_item_id)
      : [];
  if (later.length > cascade.length) {
    throw mockConflictError("Only the latest step in the trail can be removed.");
  }
  return [row, ...cascade];
};

export async function mockDeleteStoryFilter(
  storyId: string,
  filterId: string,
): Promise<StoryFilterMutationResponseApi> {
  await wait(LATENCY_MS);
  assertStoryExists(storyId);
  assertCanEdit();
  const removed = rowsRemovedWith(findRow(filterId));

  const before = sortFilters(storedFilters);
  const deletedIds = removed.map((row) => row.id);
  storedFilters = storedFilters.filter((row) => !deletedIds.includes(row.id));
  return toMutationResponse(null, deletedIds, before);
}

// ─── REWIND ────────────────────────────────────────────────────────────────────

export async function mockRewindStoryFilters(
  storyId: string,
  body: StoryFilterRewindRequestApi,
): Promise<StoryFilterMutationResponseApi> {
  await wait(LATENCY_MS);
  assertStoryExists(storyId);
  assertCanEdit();

  const trail = trailRows();
  const latestId = trail.length ? trail[trail.length - 1].id : null;
  if (body.expected_latest_id !== latestId) {
    throw mockConflictError("The trail changed since you last looked. Reload and try again.");
  }
  const target = body.to_filter_id === null ? null : trail.find((row) => row.id === body.to_filter_id);
  if (target === undefined) throw mockNotFoundError(`Trail step not found: ${body.to_filter_id}`);

  const before = sortFilters(storedFilters);
  const afterSeq = target?.seq ?? 0;
  const deletedIds = trail.filter((row) => (row.seq ?? 0) > afterSeq).map((row) => row.id);
  storedFilters = storedFilters.filter((row) => !deletedIds.includes(row.id));
  return toMutationResponse(null, deletedIds, before);
}
